import React, { ComponentType, MouseEvent, useRef, useState } from 'react';
import moment from 'moment';
import { Nullable } from 'global';
import { useTheme } from '@mui/material/styles';
import { Calendar as BigCalendar, momentLocalizer, CalendarProps, EventPropGetter } from 'react-big-calendar';

import { CalendarEvent } from './types';
import { getPriorityColor } from './CalendarEventCard';
import { CalendarWrapper } from './CalendarStylesContainer';
import { CalendarShowMorePopover } from './CalendarShowMorePopover';

// Base library styles (required for calendar layout). This is a library asset, not a project SCSS file.
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

interface ShowMoreState {
  date: Date;
  anchorEl: HTMLElement;
  events: CalendarEvent[];
}

// Parse the hidden count from the "+N more" label (e.g. "+3 more" -> 3)
const getHiddenCount = (anchor: HTMLElement, total: number): number => {
  const parsed = parseInt((anchor.textContent ?? '').replace(/\D/g, ''), 10);
  return Number.isFinite(parsed) ? parsed : total;
};

export const Calendar = ({ eventPropGetter, ...props }: Omit<CalendarProps<CalendarEvent>, 'localizer'>) => {
  const theme = useTheme();

  const showMoreAnchorRef = useRef<HTMLElement | null>(null);
  const [showMore, setShowMore] = useState<Nullable<ShowMoreState>>(null);

  const defaultEventPropGetter: EventPropGetter<CalendarEvent> = event => ({
    style: {
      borderLeft: `3px solid ${getPriorityColor(event.priority)(theme)}`,
    },
  });

  const handleClickCapture = (e: MouseEvent<HTMLDivElement>) => {
    showMoreAnchorRef.current = (e.target as HTMLElement).closest('.rbc-show-more');
  };

  const handleShowMore = (events: CalendarEvent[], date: Date) => {
    const anchorEl = showMoreAnchorRef.current;
    if (!anchorEl) return;

    const hiddenCount = getHiddenCount(anchorEl, events.length);
    const hiddenEvents = events.slice(Math.max(0, events.length - hiddenCount));

    setShowMore({ events: hiddenEvents, date, anchorEl });
  };

  const handleSelectEvent = (event: CalendarEvent, e: MouseEvent<HTMLElement>) => {
    props.onSelectEvent?.(event, e);
    setShowMore(null);
  };

  const eventComponent = props.components?.event as ComponentType<{ event: CalendarEvent }> | undefined;

  return (
    <CalendarWrapper onClickCapture={handleClickCapture}>
      <BigCalendar
        {...props}
        popup={false}
        localizer={localizer}
        endAccessor="end"
        startAccessor="start"
        onShowMore={handleShowMore}
        dayLayoutAlgorithm="no-overlap"
        eventPropGetter={eventPropGetter ?? defaultEventPropGetter}
        scrollToTime={new Date(1970, 0, 1, 8, 0, 0)}
      />

      <CalendarShowMorePopover
        date={showMore?.date ?? null}
        events={showMore?.events ?? []}
        EventComponent={eventComponent}
        anchorEl={showMore?.anchorEl ?? null}
        onClose={() => setShowMore(null)}
        onSelectEvent={(event, e) => handleSelectEvent(event, e)}
      />
    </CalendarWrapper>
  );
};
